// ABOUTME: Protected child dashboard page displaying comprehensive child progress and behavior data.
// ABOUTME: Features real-time SignalR updates, responsive layout, and comprehensive error handling with 404 support.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Snackbar,
  TablePagination,
  Paper,
  Chip,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  FlightTakeoff as FlightTakeoffIcon,
  Favorite as HeartIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { decorativeGradients, starAccent, naluV1CelebrationBannerBg, naluV1CelebrationBannerText } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { ChildDashboardHeader } from '../components/ChildDashboardHeader';
import { ChildContentRow } from '../components/ChildContentRow';
import CrossingChildSection from '../components/Crossing/CrossingChildSection';
import { useSiteSettings } from '../hooks/useSiteSettings';
import HeartCalendar from '../components/HeartCalendar';
import AnalyticsTimeChart from '../components/AnalyticsTimeChart';
import MakeItRightCelebrationModal from '../components/MakeItRightCelebrationModal';
import { familyAPI, behaviorAPI, goalsAPI, choresAPI, awayScheduleAPI } from '../services/api';
import { childDashboardService } from '../services/childDashboard';
import { getCompleteWeeksDateRange } from '../utils/calendarUtils';
import { Goal, Child, ChoreListPeriodStatus, ChoreListType, ChoreListStatus, GoalType, DailyProgress, BehaviorRecord } from '../types/api';
import { getTodayInFamilyTimezone } from '../utils/dateUtils';
import { getChildColor } from '../utils/childColorUtils';
import naluV1Theme from '../theme/theme.nalu-v1';
import { getFullApiUrl } from '../utils/routeUtils';
import { filterActiveGoals, filterUncelebratedGoals, goalToProgressIndicator, sortGoalsByProgress } from '../utils/goalUtils';
import { useCelebration } from '../hooks/useCelebration';
import { useSignalR } from '../contexts/SignalRContext';

interface BehaviorCounts {
  stars: number;
  triangles: number;
  squares: number;
  currentStage: number;
  currentPositionDisplayName?: string;
  allPositionDisplayNames?: string[];
  flipYourCoins?: number;
  oneOnOneTimes?: number;
  makeItRights?: number;
}


interface ConnectionStatus {
  isOnline: boolean;
  lastUpdate: Date | null;
  retryCount: number;
}

// Some API responses nest today's behaviors under a legacy todaysProgress wrapper
// instead of directly on the child object; this type covers that alternate shape.
type ChildWithLegacyProgress = Child & {
  todaysProgress?: { todaysBehaviors?: BehaviorRecord[] };
};

const ChildDashboardBasic: React.FC = () => {
  const { childId, urlSlug, familyId, slug, childName, token } = useParams<{
    childId?: string;
    urlSlug?: string;
    familyId?: string;
    slug?: string;
    childName?: string;
    token?: string;
  }>();
  const { user } = useAuth();
  const { celebrate } = useCelebration();
  const { on, connectionState } = useSignalR();
  const { data: siteSettings } = useSiteSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  // Refs to track fetched data and prevent redundant calls
  const fetchedChildIdRef = useRef<number | null>(null);
  const fetchedUrlSlugRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  // State management
  const [child, setChild] = useState<Child | null>(null);
  const [behaviorCounts, setBehaviorCounts] = useState<BehaviorCounts>({
    stars: 0,
    triangles: 0,
    squares: 0,
    currentStage: 1,
    flipYourCoins: 0,
    oneOnOneTimes: 0,
    makeItRights: 0,
  });
  const [goals, setGoals] = useState<Goal[]>([]);
  const [uncelebratedGoals, setUncelebratedGoals] = useState<Goal[]>([]);
  const [uncelebratedPage, setUncelebratedPage] = useState(0);
  const [uncelebratedRowsPerPage] = useState(5);
  const [chores, setChores] = useState<ChoreListPeriodStatus[]>([]);
  const [heartDates, setHeartDates] = useState<string[]>([]);
  const [mendingHeartDates, setMendingHeartDates] = useState<string[]>([]);
  const [calendarDailyProgress, setCalendarDailyProgress] = useState<DailyProgress[]>([]);
  const [selectedDateBehaviors, setSelectedDateBehaviors] = useState<BehaviorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [heartLoading, setHeartLoading] = useState(true);
  const [selectedTimelineDate, setSelectedTimelineDate] = useState<string>('');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>(() => {
    // Initialize calendar selected date to today (will update when child timezone loads)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const todayStr = getTodayInFamilyTimezone(timezone);
    const [year, month, day] = todayStr.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  });
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isOnline: true,
    lastUpdate: null,
    retryCount: 0,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);

  // Issue #1575: Index of this child in family's ordered list, used for background color
  const [familyChildIndex, setFamilyChildIndex] = useState<number>(0);

  // Celebration state for chore completion (Issue #1343)
  const [celebrationMessage, setCelebrationMessage] = useState<string | null>(null);
  const [highlightedListId, setHighlightedListId] = useState<number | null>(null);

  // Away day state (issue #1706)
  const [isTodayAwayDay, setIsTodayAwayDay] = useState(false);

  // Auto-collapse state for completed chore lists (Issue #1515)
  const [autoCollapseListIds, setAutoCollapseListIds] = useState<Set<number>>(new Set());

  // Historical chores fetched for a past date selected on the heart calendar
  const [historicalChores, setHistoricalChores] = useState<ChoreListPeriodStatus[]>([]);

  // Tracks which approved MIR list IDs have been acknowledged this session (Issues #1374/#1515)
  // Persisted in sessionStorage so dismissal survives page navigation within the same tab
  const mirSessionKey = `mir-acknowledged-${childId ?? urlSlug ?? slug ?? 'unknown'}`;
  const [acknowledgedMirListIds, setAcknowledgedMirListIds] = useState<Set<number>>(() => {
    try {
      const stored = sessionStorage.getItem(mirSessionKey);
      return stored ? new Set<number>(JSON.parse(stored)) : new Set<number>();
    } catch {
      return new Set<number>();
    }
  });

  // Nuclear reset state (Issue #1310)

  // Determine access pattern: authenticated, URL slug, or token-based
  const numericChildId = childId ? parseInt(childId, 10) : null;
  // If childId exists but is NOT numeric, it's actually a URL slug
  const isUrlSlugAccess = !!urlSlug || (childId && isNaN(parseInt(childId, 10)));
  const isTokenAccess = !!(familyId && slug && childName && token);


  // Memoize behaviorData calculation to prevent unnecessary re-renders
  const memoizedBehaviorData = useMemo(() => {
    // For anonymous access, use the selectedDateBehaviors (which includes "today" when today is selected)
    if (isUrlSlugAccess || isTokenAccess) {
      return selectedDateBehaviors;
    }

    // For authenticated access, check multiple possible locations for the behavior data
    const childBehaviors = child?.todaysBehaviors || [];
    const progressBehaviors = (child as ChildWithLegacyProgress)?.todaysProgress?.todaysBehaviors || [];

    return childBehaviors.length > 0 ? childBehaviors : progressBehaviors;
  }, [isUrlSlugAccess, isTokenAccess, selectedDateBehaviors, child]);

  // Unified heart dates loading wrapper (Issue #870)
  // Issue #1347: Added overrideTimezone parameter to fix date calculation when child state hasn't updated yet
  const loadHeartDatesUnified = useCallback(async (startDate: Date, endDate: Date, overrideChildId?: number, overrideTimezone?: string) => {
    try {
      setHeartLoading(true);
      let heartDatesResult: { heartDates: string[]; mendingHeartDates: string[] } = { heartDates: [], mendingHeartDates: [] };
      // Issue #1347: Use overrideTimezone if provided, otherwise fall back to child state
      const timezone = overrideTimezone || child?.familyTimezone;

      let progressHistory: DailyProgress[] = [];
      if (isTokenAccess && slug && token) {
        // Use token-based endpoint for tablet access (using URL slug + token for security)
        // Issue #1347: Pass familyTimezone to fix date calculation
        [heartDatesResult, progressHistory] = await Promise.all([
          childDashboardService.getHeartDatesByUrlSlugWithToken(slug, token, startDate, endDate, timezone),
          childDashboardService.getProgressHistoryByUrlSlugWithToken(slug, token, startDate, endDate, timezone),
        ]);
      } else if (isUrlSlugAccess && urlSlug) {
        // Use URL slug endpoint for unauthenticated access
        // Issue #1347: Pass familyTimezone to fix date calculation
        [heartDatesResult, progressHistory] = await Promise.all([
          childDashboardService.getHeartDatesByUrlSlug(urlSlug, startDate, endDate, timezone),
          childDashboardService.getProgressHistoryByUrlSlug(urlSlug, startDate, endDate, timezone),
        ]);
      } else {
        // Use authenticated endpoint for normal access
        const childIdToUse = overrideChildId || numericChildId;
        if (!childIdToUse) {
          setHeartDates([]);
          setMendingHeartDates([]);
          setHeartLoading(false);
          return;
        }
        // Issue #1347: Pass familyTimezone to fix date calculation
        [heartDatesResult, progressHistory] = await Promise.all([
          behaviorAPI.getChildHeartDates(childIdToUse, startDate, endDate, timezone),
          behaviorAPI.getChildProgressHistorySummary(childIdToUse, startDate, endDate, timezone),
        ]);
      }

      // Safely update state with validated data
      setHeartDates(Array.isArray(heartDatesResult.heartDates) ? heartDatesResult.heartDates : []);
      setMendingHeartDates(Array.isArray(heartDatesResult.mendingHeartDates) ? heartDatesResult.mendingHeartDates : []);
      setCalendarDailyProgress(Array.isArray(progressHistory) ? progressHistory : []);
      setConnectionStatus(prev => ({ ...prev, isOnline: true, retryCount: 0 }));
    } catch (err) {
      console.error('Error fetching heart dates:', err);
      setHeartDates([]); // Set empty array on error
      setMendingHeartDates([]);
      setConnectionStatus(prev => ({ ...prev, isOnline: false }));
    } finally {
      setHeartLoading(false);
    }
  }, [numericChildId, isUrlSlugAccess, isTokenAccess, urlSlug, token, slug, child?.familyTimezone]);

  // Legacy fetchHeartDates for backward compatibility
  // Issue #1347: Added overrideTimezone parameter to pass timezone directly from dashboard data
  const fetchHeartDates = useCallback(async (overrideChildId?: number, overrideTimezone?: string) => {
    // Calculate complete weeks date range for proper calendar display using family timezone
    // Issue #1347: Use overrideTimezone if provided, otherwise fall back to child state
    const familyTimezone = overrideTimezone || child?.familyTimezone;
    const todayStr = getTodayInFamilyTimezone(familyTimezone);
    // Use local midnight (no Z) so getCompleteWeeksDateRange's local date methods return the correct day
    const today = new Date(todayStr + 'T00:00:00');
    const { startDate, endDate } = getCompleteWeeksDateRange(today);
    await loadHeartDatesUnified(startDate, endDate, overrideChildId, overrideTimezone);
  }, [loadHeartDatesUnified, child?.familyTimezone]);

  const fetchChildData = useCallback(async (skipHeartDates = false) => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    if (isTokenAccess && slug) {
      // Don't guard on previously-fetched slug — SignalR and reconnect re-fetches need fresh data
      // if (fetchedUrlSlugRef.current === slug) return;
      
      isFetchingRef.current = true;
      // Token-based access - get data using token authentication
      try {
        const dashboardData = await childDashboardService.getDashboardDataByToken(slug, token);
        setChild({
          id: dashboardData.childId,
          childId: dashboardData.childId,
          name: dashboardData.childName,
          birthYear: new Date().getFullYear() - 10, // Default placeholder
          createdAt: new Date().toISOString(),
          familyTimezone: dashboardData.familyTimezone || 'America/Chicago',
          familyId: dashboardData.activeGoals?.[0]?.familyId || 0 // Extract familyId from goals data
        });

        // Store today's behaviors as initially selected date behaviors (since we start with today selected)
        setSelectedDateBehaviors(dashboardData.todaysBehaviors || []);

        setBehaviorCounts({
          stars: dashboardData.starCount || 0,
          triangles: dashboardData.triangleCount || 0,
          squares: dashboardData.squareCount || 0,
          currentStage: dashboardData.ladderPosition || 1,
          currentPositionDisplayName: `Stage ${dashboardData.ladderPosition || 1}`,
          allPositionDisplayNames: [],
          flipYourCoins: dashboardData.flipYourCoinCount || 0,
          oneOnOneTimes: dashboardData.oneOnOneTimeCount || 0,
          makeItRights: dashboardData.makeItRightCount || 0,
        });
        // Separate goals into active and uncelebrated completed
        const allGoals = dashboardData.activeGoals || [];
        const activeGoals = sortGoalsByProgress(filterActiveGoals(allGoals));
        const uncelebrated = filterUncelebratedGoals(allGoals);
        setGoals(activeGoals);
        setUncelebratedGoals(uncelebrated);
        setUncelebratedPage(0);
        setConnectionStatus(prev => ({ ...prev, isOnline: true, retryCount: 0 }));
        fetchedUrlSlugRef.current = slug;

        // Issue #1575: Set color index from API response for token access
        setFamilyChildIndex(dashboardData.familyChildIndex ?? 0);

        // Fetch heart dates using unified wrapper
        // Issue #1347: Pass timezone directly since child state hasn't updated yet
        // Issue #1635: Skip heart dates on refresh — hearts only change at 3am reconciliation
        if (!skipHeartDates) {
          fetchHeartDates(undefined, dashboardData.familyTimezone);
        }
      } catch (err) {
        console.error('Error fetching child dashboard data via token:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        setConnectionStatus(prev => ({ ...prev, isOnline: false }));
      } finally {
        isFetchingRef.current = false;
      }
    } else if (isUrlSlugAccess && urlSlug) {
      // Don't guard on previously-fetched URL slug — SignalR and reconnect re-fetches need fresh data
      // if (fetchedUrlSlugRef.current === urlSlug) return;
      
      isFetchingRef.current = true;
      // URL slug access - get data from our new endpoint
      try {
        const dashboardData = await childDashboardService.getDashboardDataByUrlSlug(urlSlug);
        setChild({
          id: dashboardData.childId,
          childId: dashboardData.childId,
          name: dashboardData.childName,
          birthYear: new Date().getFullYear() - 10, // Default placeholder
          createdAt: new Date().toISOString(),
          familyTimezone: dashboardData.familyTimezone || 'America/Chicago',
          familyId: dashboardData.activeGoals?.[0]?.familyId || 0 // Extract familyId from goals data
        });

        // Store today's behaviors as initially selected date behaviors (since we start with today selected)
        setSelectedDateBehaviors(dashboardData.todaysBehaviors || []);

        // Note: For URL slug access (anonymous), we don't have family timezone
        // The AnalyticsTimeChart will fall back to UTC time
        console.log('URL Slug: Updating behavior counts:', {
          stars: dashboardData.starCount || 0,
          triangles: dashboardData.triangleCount || 0,
          squares: dashboardData.squareCount || 0
        });
        setBehaviorCounts({
          stars: dashboardData.starCount || 0,
          triangles: dashboardData.triangleCount || 0,
          squares: dashboardData.squareCount || 0,
          currentStage: dashboardData.ladderPosition || 1,
          currentPositionDisplayName: `Stage ${dashboardData.ladderPosition || 1}`,
          allPositionDisplayNames: [],
          flipYourCoins: dashboardData.flipYourCoinCount || 0,
          oneOnOneTimes: dashboardData.oneOnOneTimeCount || 0,
          makeItRights: dashboardData.makeItRightCount || 0,
        });
        // Separate goals into active and uncelebrated completed
        const allGoals = dashboardData.activeGoals || [];
        const activeGoals = sortGoalsByProgress(filterActiveGoals(allGoals));
        const uncelebrated = filterUncelebratedGoals(allGoals);
        setGoals(activeGoals);
        setUncelebratedGoals(uncelebrated);
        setUncelebratedPage(0);
        setConnectionStatus(prev => ({ ...prev, isOnline: true, retryCount: 0 }));
        fetchedUrlSlugRef.current = urlSlug;

        // Issue #1575: Set color index from API response for URL slug access
        setFamilyChildIndex(dashboardData.familyChildIndex ?? 0);

        // Fetch heart dates using unified wrapper
        // Issue #1347: Pass timezone directly since child state hasn't updated yet
        // Issue #1635: Skip heart dates on refresh — hearts only change at 3am reconciliation
        if (!skipHeartDates) {
          fetchHeartDates(undefined, dashboardData.familyTimezone);
        }
      } catch (err) {
        console.error('Error fetching child dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        setConnectionStatus(prev => ({ ...prev, isOnline: false }));
      } finally {
        isFetchingRef.current = false;
      }
    } else if (numericChildId) {
      // Don't guard on previously-fetched child ID — SignalR and reconnect re-fetches need fresh data
      // if (fetchedChildIdRef.current === numericChildId) return;

      isFetchingRef.current = true;
      // Traditional authenticated access
      try {
        const childData = await familyAPI.getChild(numericChildId);
        setChild(childData);

        // Set today's behaviors for timeline (from backend TodaysBehaviors property)
        if (childData.todaysBehaviors) {
          setSelectedDateBehaviors(childData.todaysBehaviors);
        }

        setConnectionStatus(prev => ({ ...prev, isOnline: true, retryCount: 0 }));
        fetchedChildIdRef.current = numericChildId;

        // Issue #1575: Fetch sibling list to determine this child's color index
        if (childData.familyId) {
          try {
            const siblings = await familyAPI.getChildren(childData.familyId);
            const idx = siblings.findIndex(s => s.id === numericChildId || s.childId === numericChildId);
            setFamilyChildIndex(idx >= 0 ? idx : 0);
          } catch {
            // Fallback to first color if sibling list unavailable
            setFamilyChildIndex(0);
          }
        }

        // Issue #1348: Pass timezone directly since child state hasn't updated yet
        // (Consistent with URL slug/token access pattern from #1347)
        // Issue #1635: Skip heart dates on refresh — hearts only change at 3am reconciliation
        if (!skipHeartDates) {
          fetchHeartDates(undefined, childData.familyTimezone);
        }
      } catch (err) {
        console.error('Error fetching child data:', err);
        setError('Failed to load child information');
        setConnectionStatus(prev => ({ ...prev, isOnline: false }));
      } finally {
        isFetchingRef.current = false;
      }
    }
  }, [numericChildId, isUrlSlugAccess, isTokenAccess, urlSlug, token, slug, fetchHeartDates]);

  const fetchBehaviorData = useCallback(async () => {
    if (isUrlSlugAccess || isTokenAccess) return; // Skip for anonymous access - data already fetched in fetchChildData
    if (!numericChildId) return;
    
    try {
      // Let the backend determine "today" using the family's timezone
      const dailyProgress = await behaviorAPI.getDailyProgress(numericChildId);

      setBehaviorCounts({
        stars: dailyProgress.starCount || 0,
        triangles: dailyProgress.triangleCount || 0,
        squares: dailyProgress.squareCount || 0,
        currentStage: dailyProgress.currentPosition || 1,
        currentPositionDisplayName: dailyProgress.currentPositionDisplayName,
        allPositionDisplayNames: dailyProgress.allPositionDisplayNames,
        flipYourCoins: dailyProgress.flipYourCoinCount || 0,
        oneOnOneTimes: dailyProgress.oneOnOneTimeCount || 0,
        makeItRights: dailyProgress.makeItRightCount || 0,
      });
      
      setConnectionStatus(prev => ({ 
        ...prev, 
        isOnline: true, 
        lastUpdate: new Date(),
        retryCount: 0,
      }));
    } catch (err) {
      console.error('Error fetching behavior data:', err);
      setConnectionStatus(prev => ({ 
        ...prev, 
        isOnline: false,
        retryCount: prev.retryCount + 1,
      }));
      
      if (connectionStatus.retryCount === 0) {
        setSnackbarMessage('Unable to update behavior data');
        setSnackbarOpen(true);
      }
    }
  }, [numericChildId, connectionStatus.retryCount, isUrlSlugAccess, isTokenAccess]);

  const fetchGoalsData = useCallback(async () => {
    if (isUrlSlugAccess || isTokenAccess) return; // Skip for anonymous access - data already fetched in fetchChildData
    if (!numericChildId) return;

    try {
      const childGoals = await goalsAPI.getChildGoals(numericChildId);
      // Separate goals into active and uncelebrated completed
      const activeGoals = sortGoalsByProgress(filterActiveGoals(childGoals || []));
      const uncelebrated = filterUncelebratedGoals(childGoals || []);
      setGoals(activeGoals);
      setUncelebratedGoals(uncelebrated);
      setUncelebratedPage(0);
      setConnectionStatus(prev => ({ ...prev, isOnline: true, retryCount: 0 }));
    } catch (err) {
      console.error('Error fetching goals data:', err);
      setConnectionStatus(prev => ({ ...prev, isOnline: false }));
    }
  }, [numericChildId, isUrlSlugAccess, isTokenAccess]);

  const fetchChoresData = useCallback(async () => {
    try {
      let choreData: ChoreListPeriodStatus[] = [];

      if (isTokenAccess && slug && token) {
        // Token-based access
        choreData = await childDashboardService.getChoresByUrlSlug(slug, token);
      } else if (isUrlSlugAccess && urlSlug) {
        // URL slug access
        choreData = await childDashboardService.getChoresByUrlSlug(urlSlug);
      } else if (numericChildId && child?.familyId) {
        // Authenticated access
        choreData = await choresAPI.getChoreListStatuses(child.familyId, numericChildId);
      }

      // Backend returns lists already sorted: behavior-triggered lists first (newest StartDate first), then regular lists by SortOrder.
      // No client-side re-sort needed — preserving server order keeps child and family dashboards in sync.
      setChores(choreData);
      // Remove auto-collapse IDs for lists that are now incomplete (Issue #1515)
      setAutoCollapseListIds(prev => {
        const stillComplete = new Set(choreData.filter(c => c.isComplete).map(c => c.id));
        return new Set([...prev].filter(id => stillComplete.has(id)));
      });
      setConnectionStatus(prev => ({ ...prev, isOnline: true, retryCount: 0 }));
      return choreData;
    } catch (err) {
      console.error('Error fetching chores data:', err);
      // Don't set connection offline for chores errors - it may just be a 404 for no chores
      setChores([]);
      return [];
    }
  }, [numericChildId, isUrlSlugAccess, isTokenAccess, urlSlug, token, slug, child?.familyId]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (isUrlSlugAccess || isTokenAccess) {
        // For anonymous access (URL slug or token), fetchChildData handles everything including heart dates
        await fetchChildData();
        // Fetch chores separately for anonymous access
        await fetchChoresData();
      } else {
        // For authenticated access, fetch all data in parallel
        // Issue #1348: fetchHeartDates is now called from within fetchChildData
        // with the timezone from the just-fetched child data (consistent with #1347)
        await Promise.all([
          fetchChildData(),
          fetchBehaviorData(),
          fetchGoalsData(),
        ]);
        // Fetch chores after child data is available (needs familyId)
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [fetchChildData, fetchBehaviorData, fetchGoalsData, fetchChoresData, isUrlSlugAccess, isTokenAccess]);

  // Initial data load
  useEffect(() => {
    if (numericChildId || (isUrlSlugAccess && urlSlug) || (isTokenAccess && slug)) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericChildId, isUrlSlugAccess, isTokenAccess, urlSlug, token]);

  // Fetch chores when child data becomes available (for authenticated access)
  useEffect(() => {
    if (!isUrlSlugAccess && !isTokenAccess && child?.familyId && numericChildId) {
      fetchChoresData();
    }
  }, [child?.familyId, numericChildId, isUrlSlugAccess, isTokenAccess, fetchChoresData]);

  // Load today's away status (issue #1706)
  useEffect(() => {
    if (!child?.familyId) return;
    const today = child.familyTimezone
      ? getTodayInFamilyTimezone(child.familyTimezone)
      : getTodayInFamilyTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    awayScheduleAPI.getAwayDatesInRange(child.familyId, today, today)
      .then(dates => setIsTodayAwayDay(dates.length > 0))
      .catch(() => { /* non-critical */ });
  }, [child?.familyId, child?.familyTimezone]);

  // Note: Heart dates are already fetched in fetchAllData when child data is available
  // No need for a separate useEffect to fetch them again

  // Refs so SignalR handlers (registered once at mount) can always access the latest callbacks
  // without stale closures — pattern mirrors DashboardRedesigned.tsx
  // childIdRef: on token-access routes numericChildId is null (no route param), so fall back to
  // child.id once the child data loads — needed for childId-guarded handlers.
  const childIdRef = useRef<number | null | undefined>(numericChildId ?? child?.id);
  childIdRef.current = numericChildId ?? child?.id;
  const fetchBehaviorDataRef = useRef(fetchBehaviorData);
  fetchBehaviorDataRef.current = fetchBehaviorData;
  const fetchGoalsDataRef = useRef(fetchGoalsData);
  fetchGoalsDataRef.current = fetchGoalsData;
  const fetchChildDataRef = useRef(fetchChildData);
  fetchChildDataRef.current = fetchChildData;
  const fetchChoresDataRef = useRef(fetchChoresData);
  fetchChoresDataRef.current = fetchChoresData;
  const fetchAllDataRef = useRef(fetchAllData);
  fetchAllDataRef.current = fetchAllData;
  // fetchBehaviorDataForDate is defined later in the file; initialized to null and assigned after definition
  const fetchBehaviorDataForDateRef = useRef<((dateString: string) => Promise<void>) | null>(null);
  const selectedTimelineDateRef = useRef<string>(selectedTimelineDate);
  selectedTimelineDateRef.current = selectedTimelineDate;
  // Track previous connectionState to detect Disconnected→Connected transitions for re-fetch
  const prevConnectionStateRef = useRef<string>(connectionState);

  // SignalR subscriptions — relevant for authenticated parent access and token-based child access.
  // URL slug (anonymous/public sharing) users aren't connected to the hub.
  useEffect(() => {
    const unsubProgress = on('DailyProgressUpdated', (data: unknown) => {
      const progress = data as {
        childId: number;
        starCount: number;
        triangleCount: number;
        squareCount: number;
        flipYourCoinCount: number;
        makeItRightCount: number;
        oneOnOneTimeCount: number;
        currentPosition: number;
        currentPositionDisplayName: string;
      };
      // Only update if the event is for this child
      if (childIdRef.current == null || progress.childId !== childIdRef.current) return;
      setBehaviorCounts({
        stars: progress.starCount,
        triangles: progress.triangleCount,
        squares: progress.squareCount,
        currentStage: progress.currentPosition,
        currentPositionDisplayName: progress.currentPositionDisplayName,
        flipYourCoins: progress.flipYourCoinCount,
        oneOnOneTimes: progress.oneOnOneTimeCount,
        makeItRights: progress.makeItRightCount,
      });
      // For token-based child access the journey chart uses pre-fetched behavior data
      // (not its own API calls), so we must refresh the behavior list here — otherwise
      // the chart re-renders with stale data and the timeline doesn't update.
      fetchBehaviorDataForDateRef.current?.(selectedTimelineDateRef.current);
      setTimelineRefreshKey(prev => prev + 1);
    });

    const unsubGoalUpdated = on('GoalUpdated', (data: unknown) => {
      const goal = data as { childId: number };
      if (childIdRef.current == null || goal.childId !== childIdRef.current) return;
      // Anonymous access (token/slug) fetches goals via fetchChildData; authenticated uses fetchGoalsData
      if (isUrlSlugAccess || isTokenAccess) {
        fetchChildDataRef.current();
      } else {
        fetchGoalsDataRef.current();
      }
    });

    const unsubGoalDeleted = on('GoalDeleted', (data: unknown) => {
      const payload = data as { childId: number };
      if (childIdRef.current == null || payload.childId !== childIdRef.current) return;
      if (isUrlSlugAccess || isTokenAccess) {
        fetchChildDataRef.current();
      } else {
        fetchGoalsDataRef.current();
      }
    });

    const unsubChoreList = on('ChoreListUpdated', (data: unknown) => {
      const choreList = data as { childId: number };
      if (childIdRef.current == null || choreList.childId !== childIdRef.current) return;
      fetchChoresDataRef.current();
    });

    const unsubChoreDeleted = on('ChoreListDeleted', (data: unknown) => {
      const payload = data as { childId: number };
      if (childIdRef.current == null || payload.childId !== childIdRef.current) return;
      fetchChoresDataRef.current();
    });

    const unsubChoreAwaiting = on('ChoreAwaitingApproval', (data: unknown) => {
      const payload = data as { childId: number };
      if (childIdRef.current == null || payload.childId !== childIdRef.current) return;
      fetchChoresDataRef.current();
    });

    const unsubMIR = on('MakeItRightUpdated', (data: unknown) => {
      const payload = data as { childId: number };
      if (childIdRef.current == null || payload.childId !== childIdRef.current) return;
      fetchChoresDataRef.current();
    });

    const unsubRAR = on('ResetAndReflectUpdated', (data: unknown) => {
      const payload = data as { childId: number };
      if (childIdRef.current == null || payload.childId !== childIdRef.current) return;
      fetchChoresDataRef.current();
    });

    const unsubOOO = on('OneOnOneTimeUpdated', (data: unknown) => {
      const payload = data as { childId: number };
      if (childIdRef.current == null || payload.childId !== childIdRef.current) return;
      fetchChoresDataRef.current();
    });

    const unsubGoalReached = on('GoalReached', (data: unknown) => {
      const goal = data as { childId: number };
      if (childIdRef.current == null || goal.childId !== childIdRef.current) return;
      if (isUrlSlugAccess || isTokenAccess) {
        fetchChildDataRef.current();
      } else {
        fetchGoalsDataRef.current();
      }
    });

    const unsubBehaviorRecorded = on('BehaviorRecorded', (_data: unknown) => {
      // DailyProgressUpdated fires immediately after and handles the timeline refresh
    });

    const unsubJourneyUpdated = on('JourneyUpdated', (data: unknown) => {
      const journey = data as { childId: number; position: number; positionDisplayName: string };
      if (childIdRef.current == null || journey.childId !== childIdRef.current) return;
      setBehaviorCounts(current => ({
        ...current,
        currentStage: journey.position,
        currentPositionDisplayName: journey.positionDisplayName,
      }));
    });

    const unsubPeriodReset = on('PeriodReset', () => {
      fetchAllDataRef.current();
    });

    return () => {
      unsubProgress();
      unsubBehaviorRecorded();
      unsubGoalUpdated();
      unsubGoalDeleted();
      unsubChoreList();
      unsubChoreDeleted();
      unsubChoreAwaiting();
      unsubMIR();
      unsubRAR();
      unsubOOO();
      unsubGoalReached();
      unsubJourneyUpdated();
      unsubPeriodReset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  // Re-fetch all data when SignalR reconnects (Disconnected→Connected transition).
  useEffect(() => {
    if (prevConnectionStateRef.current !== 'Connected' && connectionState === 'Connected') {
      fetchAllDataRef.current();
    }
    prevConnectionStateRef.current = connectionState;
  }, [connectionState]);

  // Update selectedTimelineDate and selectedCalendarDate to use family timezone when child data is loaded.
  // Gated on child ID (not the child object itself) so SignalR-triggered refetches of the SAME
  // child — which create a new child object on every call — don't repeatedly snap the calendar
  // back to today and interrupt a user who has navigated the Heart Calendar to a previous month.
  const dateInitializedForChildIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (child && dateInitializedForChildIdRef.current !== childIdRef.current) {
      dateInitializedForChildIdRef.current = childIdRef.current;
      // Use family timezone if available, otherwise fallback to browser timezone
      const timezone = child.familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const todayInFamilyTimezone = getTodayInFamilyTimezone(timezone);
      setSelectedTimelineDate(todayInFamilyTimezone);

      // Also update calendar selected date
      const [year, month, day] = todayInFamilyTimezone.split('-').map(Number);
      setSelectedCalendarDate(new Date(year, month - 1, day));
    }
  }, [child]);

  const fetchBehaviorDataForDate = useCallback(async (dateString: string) => {
    if (!isTokenAccess || !slug || !token) return;

    try {
      const timezone = child?.familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const behaviors = await childDashboardService.getBehaviorsByDateWithToken(slug, token, dateString, timezone);
      setSelectedDateBehaviors(behaviors);
    } catch (error) {
      console.error('Failed to load behavior data for date:', dateString, error);
      // Clear behaviors on error to prevent showing stale data
      setSelectedDateBehaviors([]);
    }
  }, [isTokenAccess, slug, token, child?.familyTimezone]);
  // Keep the ref current so the SignalR handler (registered once at mount) can call the latest version
  fetchBehaviorDataForDateRef.current = fetchBehaviorDataForDate;

  // Derived: whether the heart calendar is showing a historical (non-today) date
  // Use selectedTimelineDate (YYYY-MM-DD string already in family tz) instead of converting
  // selectedCalendarDate (browser-local midnight) through the family tz, which produces the
  // wrong calendar day for families behind UTC (e.g. Hawaii shows May 4 when it's May 5).
  const isViewingHistoricalDate = useMemo(() => {
    if (!child?.familyTimezone || isUrlSlugAccess || isTokenAccess) return false;
    const tz = child.familyTimezone;
    return selectedTimelineDate !== getTodayInFamilyTimezone(tz);
  }, [child?.familyTimezone, selectedTimelineDate, isUrlSlugAccess, isTokenAccess]);

  // Label shown next to "Responsibilities" header when viewing a past date (e.g. "Apr 15")
  const viewDateLabel = useMemo(() => {
    if (!isViewingHistoricalDate || !selectedTimelineDate) return undefined;
    const [year, month, day] = selectedTimelineDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [isViewingHistoricalDate, selectedTimelineDate]);

  // Fetch historical chore statuses when viewing a non-today date (authenticated only)
  useEffect(() => {
    if (!isViewingHistoricalDate || !numericChildId || !child?.familyId || !selectedTimelineDate) return;
    choresAPI.getChoreListStatuses(child.familyId, numericChildId, selectedTimelineDate)
      .then(setHistoricalChores)
      .catch(() => setHistoricalChores([]));
  }, [isViewingHistoricalDate, selectedTimelineDate, numericChildId, child?.familyId]);

  // Return to today: reset calendar and timeline to current date
  const handleReturnToToday = useCallback(() => {
    const tz = child?.familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const todayStr = getTodayInFamilyTimezone(tz);
    setSelectedTimelineDate(todayStr);
    const [year, month, day] = todayStr.split('-').map(Number);
    setSelectedCalendarDate(new Date(year, month - 1, day));
  }, [child?.familyTimezone]);

  // The chore list shown in ChildContentRow — historical when not on today, current otherwise
  const displayChores = isViewingHistoricalDate ? historicalChores : chores;

  // Fetch behavior data when timeline date changes in token access mode
  useEffect(() => {
    if (isTokenAccess && selectedTimelineDate && child?.familyTimezone) {
      fetchBehaviorDataForDate(selectedTimelineDate);
    }
  }, [selectedTimelineDate, isTokenAccess, fetchBehaviorDataForDate, child?.familyTimezone]);

  // When selectedDateBehaviors updates (after a SignalR-triggered refetch), bump the refresh key
  // so AnalyticsTimeChart re-runs its effect with the new behavior data already in place.
  useEffect(() => {
    if (isTokenAccess || isUrlSlugAccess) {
      setTimelineRefreshKey(prev => prev + 1);
    }
  }, [selectedDateBehaviors, isTokenAccess, isUrlSlugAccess]);

  // Calendar interaction handlers (Issue #870)
  const handleCalendarDaySelect = useCallback(async (date: Date) => {
    setSelectedCalendarDate(date);
    // HeartCalendar constructs dates as local midnight (new Date(year, month, day)).
    // Use local date components directly to get the calendar date the user clicked,
    // rather than converting through the family timezone which produces the wrong day
    // for families west of the browser's timezone (e.g. Hawaii shows May 4 when May 5).
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const timezone = child?.familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    setSelectedTimelineDate(dateString);

    // Load behavior data for the selected date if anonymous access
    if (isUrlSlugAccess || isTokenAccess) {
      try {
        let behaviors: BehaviorRecord[] = [];
        if (isTokenAccess && slug && token) {
          behaviors = await childDashboardService.getBehaviorsByDateWithToken(
            slug,
            token,
            dateString,
            timezone
          );
        } else if (isUrlSlugAccess && urlSlug) {
          behaviors = await childDashboardService.getBehaviorsByDateWithUrlSlug(
            urlSlug,
            dateString,
            timezone
          );
        }
        setSelectedDateBehaviors(behaviors);
      } catch (error) {
        console.error('Failed to load behavior data for selected date:', error);
        setSelectedDateBehaviors([]);
      }
    }
  }, [child?.familyTimezone, isUrlSlugAccess, isTokenAccess, slug, token, urlSlug]);

  const handleCalendarDateRangeChange = useCallback((startDate: Date, endDate: Date) => {
    // Load heart dates for the new date range using unified wrapper
    // Only load if we have the required data to make the call
    if ((isTokenAccess && slug && token) || (isUrlSlugAccess && urlSlug) || numericChildId) {
      loadHeartDatesUnified(startDate, endDate);
    }
  }, [loadHeartDatesUnified, isTokenAccess, isUrlSlugAccess, numericChildId, slug, token, urlSlug]);

  // Handle chore item completion toggle
  const handleChoreItemToggle = useCallback(async (choreListId: number, itemId: number, isCurrentlyComplete: boolean) => {
    try {
      let starAwarded = false;
      let choreListTitle: string | undefined;

      let listPendingApproval = false;

      if (isTokenAccess && slug && token) {
        // Token-based access
        if (isCurrentlyComplete) {
          await childDashboardService.markChoreItemIncomplete(slug, choreListId, itemId, token);
        } else {
          const slugResult = await childDashboardService.markChoreItemComplete(slug, choreListId, itemId, token);
          if (slugResult.listPendingApproval && slugResult.choreListTitle) {
            celebrate({
              message: `Great job! ${slugResult.choreListTitle} complete — waiting for parent approval!`,
              onToast: setCelebrationMessage
            });
          }
        }
      } else if (isUrlSlugAccess && urlSlug) {
        // URL slug access
        if (isCurrentlyComplete) {
          await childDashboardService.markChoreItemIncomplete(urlSlug, choreListId, itemId);
        } else {
          const slugResult = await childDashboardService.markChoreItemComplete(urlSlug, choreListId, itemId);
          if (slugResult.listPendingApproval && slugResult.choreListTitle) {
            celebrate({
              message: `Great job! ${slugResult.choreListTitle} complete — waiting for parent approval!`,
              onToast: setCelebrationMessage
            });
          }
        }
      } else if (numericChildId) {
        // Authenticated access - this returns star info
        if (isCurrentlyComplete) {
          await choresAPI.markItemIncomplete(choreListId, itemId);
        } else {
          const result = await choresAPI.markItemComplete(choreListId, itemId);
          starAwarded = result.starAwarded;
          choreListTitle = result.choreListTitle;
          listPendingApproval = result.listPendingApproval ?? false;
        }
      }

      // Refresh chores data — returns updated list for post-completion checks
      const updatedChores = await fetchChoresData();

      // Check if star was awarded OR if list is pending approval (RequiresApproval)
      if (starAwarded && choreListTitle) {
        setHighlightedListId(choreListId);
        setTimeout(() => setHighlightedListId(null), 2000); // Clear after 2s

        celebrate({
          message: `Great job! ${choreListTitle} complete - you earned a star!`,
          onToast: setCelebrationMessage
        });
        // Trigger auto-collapse for this list after celebration (Issue #1515)
        setAutoCollapseListIds(prev => new Set(prev).add(choreListId));
      } else if (listPendingApproval && choreListTitle) {
        // List complete but needs parent approval — still celebrate!
        setHighlightedListId(choreListId);
        setTimeout(() => setHighlightedListId(null), 2000);

        celebrate({
          message: `Great job! ${choreListTitle} complete — waiting for parent approval!`,
          onToast: setCelebrationMessage
        });
      } else if (!isCurrentlyComplete) {
        // Check if a MIR list became complete (MIR lists don't award stars) (Issue #1515)
        const updatedList = updatedChores?.find(c => c.id === choreListId);
        if (updatedList?.type === ChoreListType.MakeItRight && updatedList.isComplete) {
          setAutoCollapseListIds(prev => new Set(prev).add(choreListId));
        }
        // Simple completion message for non-star cases
        setSnackbarMessage('Item completed!');
        setSnackbarOpen(true);
      } else {
        // Unmarking complete
        setSnackbarMessage('Item marked incomplete');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error toggling chore item:', err);
      setSnackbarMessage('Failed to update item');
      setSnackbarOpen(true);
    }
  }, [isTokenAccess, isUrlSlugAccess, numericChildId, slug, token, urlSlug, fetchChoresData, celebrate]);


  // Retry mechanism for failed connections
  useEffect(() => {
    if (!connectionStatus.isOnline && connectionStatus.retryCount > 0 && connectionStatus.retryCount < 5) {
      const retryDelay = Math.min(1000 * Math.pow(2, connectionStatus.retryCount), 30000); // Exponential backoff
      
      const retryTimeout = setTimeout(() => {
        fetchBehaviorData();
      }, retryDelay);

      return () => clearTimeout(retryTimeout);
    }
  }, [connectionStatus.isOnline, connectionStatus.retryCount, fetchBehaviorData]);

  // Redirect if not authenticated (skip for anonymous access)
  if (!isUrlSlugAccess && !isTokenAccess && !user) {
    return <Navigate to="/login" replace />;
  }

  // Validate parameters based on access type
  if (!isUrlSlugAccess && !isTokenAccess && (!numericChildId || isNaN(numericChildId))) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Invalid Child ID</Typography>
          <Typography>The child ID in the URL is not valid.</Typography>
        </Alert>
      </Container>
    );
  }

  if (isUrlSlugAccess && !urlSlug) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Invalid Dashboard Link</Typography>
          <Typography>The dashboard link is not valid.</Typography>
        </Alert>
      </Container>
    );
  }

  if (isTokenAccess && (!familyId || !slug || !childName || !token)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Invalid Dashboard Link</Typography>
          <Typography>The dashboard link is not valid.</Typography>
        </Alert>
      </Container>
    );
  }

  // Show loading spinner while initializing
  if (loading && !child) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Show error state
  if (error && !child) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Dashboard</Typography>
          <Typography>{error}</Typography>
          <Box mt={2}>
            <IconButton onClick={() => window.location.reload()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Alert>
      </Container>
    );
  }

  // Show 404 if child not found
  if (!loading && !child) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="h6">Child Not Found</Typography>
          <Typography>The requested child dashboard could not be found.</Typography>
        </Alert>
      </Container>
    );
  }

  const approvedMakeItRightLists = chores.filter(
    c => c.type === ChoreListType.MakeItRight && c.status === ChoreListStatus.Finalized
  );

  // Icon + border/button accent per goal type — matches the mockup's per-card coloring
  // on the Ready to Celebrate row (issue #1900). Mirrors DashboardRedesigned's helpers.
  const getGoalTypeIcon = (type: GoalType, fontSize = 16) => {
    switch (type) {
      case GoalType.HeartStreak:
      case GoalType.HeartCollection:
        return <HeartIcon sx={{ color: theme.palette.behaviorHeart, fontSize }} />;
      case GoalType.StarCollection:
        return <StarIcon sx={{ color: starAccent, fontSize }} />;
      default:
        return <TrophyIcon sx={{ color: theme.palette.secondary.main, fontSize }} />;
    }
  };

  const getGoalCelebrationAccent = (type: GoalType) => {
    switch (type) {
      case GoalType.HeartStreak:
      case GoalType.HeartCollection:
        return { border: alpha(theme.palette.behaviorHeart, 0.6), button: theme.palette.behaviorHeart };
      case GoalType.StarCollection:
        return { border: theme.palette.primary.light, button: theme.palette.primary.dark };
      default:
        return { border: theme.palette.secondary.light, button: theme.palette.secondary.main };
    }
  };

  return (
    <>
      {/* Fixed sky background — non-scrolling, matching the parent dashboard's treatment (issue #1900) */}
      {isNaluV1Theme && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: -1,
            background: 'url(/nalu/bg-morning-37.png) center / cover no-repeat',
          }}
        />
      )}
    <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
      {/* Issue #1575: Colored Paper background matching home dashboard child card treatment.
          nalu-v1 skips this wash entirely — the mockup (Keiki Mode) gives every section its
          own neutral flat card instead of tinting the whole page (issue #1900). */}
      <Paper
        elevation={isNaluV1Theme ? 0 : 2}
        sx={isNaluV1Theme ? {
          bgcolor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        } : {
          bgcolor: getChildColor(familyChildIndex),
          p: 2,
        }}
      >
      {/* Row 1: Unified header bar — back arrow + avatar/name | teeter totter | behavior squares */}
      {child && (
        <ChildDashboardHeader
          childName={child.name}
          profileImageUrl={child.profileImageUrl}
          behaviorCounts={behaviorCounts}
          loading={loading}
          showBackButton={!isUrlSlugAccess && !isTokenAccess}
          familyTimezone={child.familyTimezone}
        />
      )}

      {/* Crossing: Resets-only list + Reset control, slug+token access only (Issue #1939) */}
      {isTokenAccess && slug && siteSettings?.crossingEnabled && (
        <CrossingChildSection slug={slug} token={token} familyTimezone={child?.familyTimezone} />
      )}

      {/* Away day banner (issue #1706) */}
      {isTodayAwayDay && (
        <Alert
          severity="info"
          icon={<FlightTakeoffIcon />}
          sx={{ mb: 2 }}
        >
          You&apos;re at the other house today — no worries about hearts or chores!
        </Alert>
      )}

      {/* Row 2: Heart Calendar + Daily Timeline.
          nalu-v1 (Keiki Mode mockup): a fixed 360px calendar column + flexible timeline
          column, each its own flat card — not one shared Card wrapping a flex row, which
          is what the other themes still use (issue #1900). */}
      {child && selectedTimelineDate && (
        isNaluV1Theme ? (
          <Box
            data-testid="section-timeline-journey"
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: '20px' }}
          >
            <HeartCalendar
              heartDates={heartDates}
              mendingHeartDates={mendingHeartDates}
              loading={heartLoading}
              viewMode={isMobile ? 'week' : 'month'}
              localStorageKey={isMobile ? `heart-calendar-expanded-child-${child.childId}` : undefined}
              familyTimezone={child.familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              selectedDate={selectedCalendarDate}
              onDaySelect={handleCalendarDaySelect}
              onDateRangeChange={handleCalendarDateRangeChange}
              dailyProgress={calendarDailyProgress}
            />
            <Paper elevation={1} sx={{ p: '20px' }}>
              <AnalyticsTimeChart
                child={child}
                selectedDate={selectedTimelineDate}
                familyId={child.familyId || 0}
                familyTimezone={child.familyTimezone}
                isAdminContext={false}
                isAnonymousAccess={isUrlSlugAccess || isTokenAccess}
                behaviorData={memoizedBehaviorData}
                hideTitle={false}
                refreshKey={timelineRefreshKey}
              />
            </Paper>
          </Box>
        ) : (
          <Card data-testid="section-timeline-journey" sx={{ mb: 3, p: 2 }}>
            <CardContent>
              {/* Calendar + Timeline side-by-side (matches parent dashboard layout) */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: { md: 'flex-start' },
                }}
              >
                {/* Heart Calendar — 1/3 width on desktop, full width on mobile */}
                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 33%' } }}>
                  <HeartCalendar
                    heartDates={heartDates}
                    mendingHeartDates={mendingHeartDates}
                    loading={heartLoading}
                    viewMode={isMobile ? 'week' : 'month'}
                    localStorageKey={isMobile ? `heart-calendar-expanded-child-${child.childId}` : undefined}
                    familyTimezone={child.familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                    selectedDate={selectedCalendarDate}
                    onDaySelect={handleCalendarDaySelect}
                    onDateRangeChange={handleCalendarDateRangeChange}
                    dailyProgress={calendarDailyProgress}
                  />
                </Box>

                {/* Daily Timeline Chart — 2/3 width on desktop, full width on mobile */}
                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 66%' } }}>
                <AnalyticsTimeChart
                  child={child}
                  selectedDate={selectedTimelineDate}
                  familyId={child.familyId || 0}
                  familyTimezone={child.familyTimezone}
                  isAdminContext={false}
                  isAnonymousAccess={isUrlSlugAccess || isTokenAccess}
                  behaviorData={memoizedBehaviorData}
                  hideTitle={false}
                  refreshKey={timelineRefreshKey}
                />
                </Box>
              </Box>
            </CardContent>
          </Card>
        )
      )}

      {/* Row 3: Goals / Schedule / Chores (same layout as parent dashboard) */}
      {child && (
        <Box mb={isNaluV1Theme ? 0 : 3} data-testid="section-content-row">
          {isViewingHistoricalDate && viewDateLabel && (
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
              <Chip
                data-testid="viewing-historical-banner"
                label={`Viewing ${viewDateLabel} — Return to today`}
                onClick={handleReturnToToday}
                color="info"
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          )}
          <ChildContentRow
            goals={goals.map(goalToProgressIndicator)}
            childId={child.childId ?? child.id ?? 0}
            showCalendar={!isUrlSlugAccess && !isTokenAccess && !!user?.isAdmin}
            choreStatuses={displayChores}
            onChoreItemToggle={(listId, itemId, isComplete) =>
              handleChoreItemToggle(listId, itemId, isComplete)
            }
            autoCollapseListIds={autoCollapseListIds}
            highlightedListId={highlightedListId}
            familyTimezone={child.familyTimezone}
            isHistoricalView={isViewingHistoricalDate}
            calendarExternalDate={selectedCalendarDate}
            viewDateLabel={viewDateLabel}
          />
        </Box>
      )}

      {/* Row 4: Completed but not Celebrated Goals */}
      {uncelebratedGoals.length > 0 && (
        isNaluV1Theme ? (
          <Box
            data-testid="section-uncelebrated-goals"
            sx={{ bgcolor: naluV1CelebrationBannerBg, borderRadius: '24px', p: { xs: '20px', md: '28px 32px' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', mb: '16px' }}>
              <Typography sx={{ fontFamily: theme.typography.h6.fontFamily, fontSize: '24px', fontWeight: 600 }}>
                ✦ Goals to celebrate!
              </Typography>
              <Typography sx={{ fontSize: '13px', color: naluV1CelebrationBannerText }}>
                you did these — time to claim them
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: '12px' }}>
              {uncelebratedGoals.map((goal) => {
                const accent = getGoalCelebrationAccent(goal.type);
                const doneDateLabel = goal.completedAt
                  ? new Date(goal.completedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      ...(child?.familyTimezone ? { timeZone: child.familyTimezone } : {}),
                    })
                  : undefined;
                return (
                  <Box
                    key={goal.id}
                    data-testid={`uncelebrated-goal-${goal.id}`}
                    sx={{
                      bgcolor: 'background.default',
                      borderRadius: '16px',
                      p: '16px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '14px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                      {getGoalTypeIcon(goal.type, 22)}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography data-testid={`goal-title-${goal.id}`} sx={{ fontWeight: 800, fontSize: '14.5px' }}>
                          {goal.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', mt: '5px' }}>
                          {doneDateLabel && (
                            <Typography
                              component="span"
                              sx={{ fontSize: '10.5px', fontWeight: 800, color: 'primary.dark', bgcolor: 'info.main', px: '9px', py: '3px', borderRadius: '999px' }}
                            >
                              Done {doneDateLabel}
                            </Typography>
                          )}
                          {(goal.rewardDescription || goal.reward) && (
                            <Typography component="span" sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                              {goal.rewardDescription || goal.reward}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => celebrate({ message: `Yay! ${goal.title} — you did it!`, onToast: setCelebrationMessage })}
                      sx={{
                        bgcolor: accent.button,
                        color: 'background.paper',
                        fontWeight: 800,
                        fontSize: '12.5px',
                        px: '16px',
                        py: '9px',
                        borderRadius: '999px',
                        flexShrink: 0,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: accent.button, boxShadow: 'none', opacity: 0.9 },
                      }}
                    >
                      Celebrate ✦
                    </Button>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
        <Paper
          data-testid="section-uncelebrated-goals"
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            background: decorativeGradients.heroPurple,
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url(data:image/svg+xml,"%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.3,
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                🎉 Goals to Celebrate!
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {uncelebratedGoals.slice(uncelebratedPage * uncelebratedRowsPerPage, (uncelebratedPage + 1) * uncelebratedRowsPerPage).map((goal) => (
                <Box
                  key={goal.id}
                  data-testid={`uncelebrated-goal-${goal.id}`}
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                    borderRadius: 2,
                    p: 2,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    data-testid={`goal-title-${goal.id}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <span data-testid="celebration-icon">🏆</span>
                    {goal.title}
                  </Typography>
                  {goal.completedAt && (
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={`Completed ${new Date(goal.completedAt).toLocaleDateString('en-US', child?.familyTimezone ? { timeZone: child.familyTimezone } : {})}`}
                        size="small"
                        sx={{
                          backgroundColor: alpha(theme.palette.success.main, 0.3),
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}
                  {/* Reward image — shown prominently when present, independent of text */}
                  {goal.rewardImageUrl && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                      <Box
                        component="img"
                        src={getFullApiUrl(goal.rewardImageUrl)}
                        alt={`Reward image for ${goal.title}`}
                        sx={{
                          width: 180,
                          height: 180,
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: `3px solid ${alpha(theme.palette.common.white, 0.8)}`,
                          animation: 'rewardGlow 2s ease-in-out infinite',
                          '@keyframes rewardGlow': {
                            '0%, 100%': { boxShadow: `0 0 15px 3px ${alpha(theme.palette.common.white, 0.4)}` },
                            '50%': { boxShadow: `0 0 30px 8px ${alpha(theme.palette.common.white, 0.7)}` },
                          },
                        }}
                      />
                    </Box>
                  )}
                  {/* Reward text — shown when present */}
                  {(goal.rewardDescription || goal.reward) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                        🎁 {goal.rewardDescription || goal.reward}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            {uncelebratedGoals.length > uncelebratedRowsPerPage && (
              <TablePagination
                component="div"
                count={uncelebratedGoals.length}
                page={uncelebratedPage}
                onPageChange={(_event, newPage) => setUncelebratedPage(newPage)}
                rowsPerPage={uncelebratedRowsPerPage}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[]}
                data-testid="uncelebrated-goals-pagination"
                aria-label="Uncelebrated goals pagination"
                sx={{
                  color: 'white',
                  '& .MuiTablePagination-actions': { color: 'white' },
                  '& .MuiTablePagination-selectIcon': { color: 'white' },
                  '& .MuiIconButton-root.Mui-disabled': { color: alpha(theme.palette.common.white, 0.4) },
                }}
              />
            )}
          </Box>
        </Paper>
        )
      )}

      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Celebration Toast for chore completion with star award (Issue #1343) */}
      <Snackbar
        open={!!celebrationMessage}
        autoHideDuration={4000}
        onClose={() => setCelebrationMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCelebrationMessage(null)}
          severity="success"
          sx={{ width: '100%', fontSize: '1.1rem' }}
        >
          {celebrationMessage}
        </Alert>
      </Snackbar>

      {/* MakeItRight Celebration Modal (Issues #1374/#1515) */}
      {(() => {
        const unacknowledgedMirLists = approvedMakeItRightLists.filter(l => !acknowledgedMirListIds.has(l.id));
        return (
          <MakeItRightCelebrationModal
            isOpen={unacknowledgedMirLists.length > 0}
            approvedLists={unacknowledgedMirLists}
            childName={child?.name || ''}
            onClose={() => {
              // Persist acknowledgement so modal does not reappear on navigation
              const next = new Set(acknowledgedMirListIds);
              unacknowledgedMirLists.forEach(l => next.add(l.id));
              setAcknowledgedMirListIds(next);
              try {
                sessionStorage.setItem(mirSessionKey, JSON.stringify([...next]));
              } catch { /* sessionStorage unavailable */ }
              celebrate({
                message: 'You made it right! Great job!',
                onToast: setCelebrationMessage,
              });
              // Trigger auto-collapse for the newly acknowledged MIR lists (Issue #1515)
              setAutoCollapseListIds(prev => {
                const updated = new Set(prev);
                unacknowledgedMirLists.forEach(l => updated.add(l.id));
                return updated;
              });
            }}
          />
        );
      })()}

    </Container>
    </>
  );
};

export default ChildDashboardBasic;